import React from 'react';
import { IconBrandGoogle } from '@tabler/icons-react';

import { auth, googleProvider } from '../Recipe/Firebase/InitialiseFirebase';
import { Button, Field, Modal, useToast } from '../../design/components';
import {
  AuthMode,
  friendlyAuthError,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from './authHelpers';

export interface AuthDialogProps {
  open: boolean;
  onClose: () => void;
}

const AuthDialog: React.FC<AuthDialogProps> = ({ open, onClose }) => {
  const { show } = useToast();
  const [mode, setMode] = React.useState<AuthMode>('signin');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [inlineError, setInlineError] = React.useState<string | null>(null);

  // Reset state whenever the dialog reopens.
  React.useEffect(() => {
    if (open) {
      setMode('signin');
      setEmail('');
      setPassword('');
      setInlineError(null);
      setLoading(false);
    }
  }, [open]);

  const handleGoogle = async () => {
    setInlineError(null);
    setLoading(true);
    try {
      await signInWithGoogle(auth, googleProvider);
      onClose();
    } catch (err) {
      const msg = friendlyAuthError(err);
      show(msg, 'error');
      setInlineError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInlineError(null);
    if (!email.trim() || !password) {
      setInlineError('Please enter an email and password.');
      return;
    }
    setLoading(true);
    try {
      if (mode === 'signin') {
        await signInWithEmail(auth, email.trim(), password);
      } else {
        await signUpWithEmail(auth, email.trim(), password);
      }
      onClose();
    } catch (err) {
      const msg = friendlyAuthError(err);
      show(msg, 'error');
      setInlineError(msg);
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = mode === 'signin' ? 'Sign in' : 'Create account';

  return (
    <Modal open={open} onClose={onClose} title="Sign in" size="sm">
      <div className="rr-auth">
        <Button
          variant="primary"
          onClick={handleGoogle}
          disabled={loading}
          leftIcon={<IconBrandGoogle size={18} />}
          className="rr-auth-block"
          type="button"
        >
          Continue with Google
        </Button>

        <div className="rr-auth-divider" aria-hidden="true">
          <span>or</span>
        </div>

        <div className="rr-auth-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signin'}
            className={[
              'rr-auth-tab',
              mode === 'signin' ? 'rr-auth-tab-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setMode('signin')}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'signup'}
            className={[
              'rr-auth-tab',
              mode === 'signup' ? 'rr-auth-tab-active' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => setMode('signup')}
          >
            Create account
          </button>
        </div>

        <form onSubmit={handleEmailSubmit} className="rr-auth-form">
          <Field
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
          />
          <Field
            label="Password"
            type="password"
            autoComplete={
              mode === 'signin' ? 'current-password' : 'new-password'
            }
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
          />
          {inlineError && (
            <div className="rr-auth-error" role="alert">
              {inlineError}
            </div>
          )}
          <Button
            variant="tomato"
            type="submit"
            loading={loading}
            disabled={loading}
            className="rr-auth-block"
          >
            {submitLabel}
          </Button>
        </form>
      </div>
    </Modal>
  );
};

export default AuthDialog;
